import React, { useState, useEffect, useMemo } from "react";
import "./ProductDescr.css";
import api from "../../api";

export default function ProductDescr({
  isArabicVinyl = false,
  titleEN,
  titleAR,
  descriptionEN,
  descriptionAR,
  artistEn,
  artistAr,
}) {
  const hasArabicDesc = Boolean(descriptionAR && descriptionAR.trim().length > 0);
  const showDual = isArabicVinyl && hasArabicDesc;

  const [wikiData, setWikiData] = useState({
    text: "",
    link: "",
  });
  const [loading, setLoading] = useState(false);

  const rawDescriptionEN = (descriptionEN || "").trim();

  const isWikipediaUrl = useMemo(() => {
    return /^https?:\/\/([a-z]+\.)?wikipedia\.org\/wiki\/.+/i.test(rawDescriptionEN);
  }, [rawDescriptionEN]);

  const hasManualDescription = rawDescriptionEN !== "" && !isWikipediaUrl;
  const shouldFetchWiki = !hasManualDescription;

  useEffect(() => {
    let ignore = false;

    const fetchDescription = async () => {
      try {
        setLoading(true);

        const res = await api.post("wikitrack/description", {
          wikiUrl: isWikipediaUrl ? rawDescriptionEN : "",
          titleEN,
          titleAR,
          artistEn,
          artistAr,
        });

        if (!ignore) {
          setWikiData({
            text: res?.data?.text || "",
            link: res?.data?.link || (isWikipediaUrl ? rawDescriptionEN : ""),
          });
        }
      } catch (e) {
        console.error("Wikipedia fetch error:", e);

        if (!ignore) {
          setWikiData({
            text: "",
            link: isWikipediaUrl ? rawDescriptionEN : "",
          });
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    if (!shouldFetchWiki) {
      setWikiData({ text: "", link: "" });
      setLoading(false);
      return;
    }

    fetchDescription();

    return () => {
      ignore = true;
    };
  }, [shouldFetchWiki, isWikipediaUrl, rawDescriptionEN, titleEN, titleAR, artistEn, artistAr]);

  const finalDescription = hasManualDescription ? rawDescriptionEN : wikiData.text;
  const finalWikiLink = hasManualDescription ? "" : wikiData.link;

  const renderDescription = (text, link) => {
    if (loading && !text) {
      return <p className="desc__text">Loading description...</p>;
    }

    if (!text) {
      return <p className="desc__text"></p>;
    }

    return (
      <p className="desc__text">
        {text}
        {link && (
          <>
            {" "}
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="desc__wikiLink"
            >
              Read more on Wikipedia
            </a>
          </>
        )}
      </p>
    );
  };

  return (
    <section className={`desc ${showDual ? "desc--dual" : "desc--single"}`}>
      <div className="desc__wrap">
        <h2 className="desc__heading">Description</h2>

        {showDual ? (
          <div className="desc__grid">
            <div className="desc__col">
              <h3 className="desc__title">{titleEN}</h3>
              {renderDescription(finalDescription, finalWikiLink)}
            </div>

            <div className="desc__divider" aria-hidden="true" />

            <div className="desc__col desc__col--ar" dir="rtl" lang="ar">
              <h3 className="desc__title">{titleAR}</h3>
              <p className="desc__text">{descriptionAR}</p>
            </div>
          </div>
        ) : (
          <div className="desc__single">
            <h3 className="desc__title">{titleEN}</h3>
            {renderDescription(finalDescription, finalWikiLink)}
          </div>
        )}
      </div>
    </section>
  );
}